/**
 * Request and response types for the portal JSON API. Copied field for
 * field from docs/api/portal-api.yaml (OpenAPI 3.1), the single source of
 * truth for the contract; internal/portal's handlers implement exactly
 * this. Do not rename or reshape a field to read better in TypeScript
 * without updating the contract first.
 *
 * Every route lives under the server prefix /api/portal (see the
 * `servers` block in the contract); src/api/httpClient.ts is the only
 * place that prepends it.
 */

export type SessionKind = "anon" | "partner" | "staff";

/**
 * GET /session/me (any caller, anonymous included) and every successful
 * login response. `kind` is "anon" when there is no session at all: this
 * call never 401s, unlike every other route.
 */
export interface Me {
  kind: SessionKind;
  /** Member ID or staff username; absent when kind is "anon". */
  subject?: string;
  /**
   * A display-only name (manager decision 2026-09-11): the member ID for a
   * partner, the local username for a locally authenticated staff member,
   * or the Keycloak full name for an OIDC staff member. Never used for
   * authorisation. Absent when kind is "anon".
   */
  displayName?: string;
  mustChangePassword: boolean;
  /** Mirrors the mft_csrf cookie; echo it back as X-CSRF-Token on every state-changing call. */
  csrfToken: string;
  /**
   * Which staff sign-in method is active on this deployment (PORTAL_STAFF_AUTH),
   * present for every caller, anonymous included, so the login page knows
   * which staff UI to show before anyone signs in. Optional here because
   * the BFF is still rolling this field out; treat a missing value as
   * "local" (this client's own default, not the BFF's).
   */
  staffAuth?: "oidc" | "local";
}

/** POST /session/login and POST /session/login/staff request body. */
export interface LoginRequest {
  id: string;
  password: string;
}

/**
 * POST /session/logout response. `endSessionUrl` is ADR 0008 section 3's
 * "for staff, returns the IdP end-session URL for the SPA to redirect to
 * when the IdP offers one"; optional because it is not part of the
 * contract's plain `Ok` response yet and a partner logout never has one.
 */
export interface LogoutResponse {
  ok: true;
  endSessionUrl?: string;
}

/** POST /password/change request body. */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface FileInfo {
  name: string;
  size: number;
  /** ISO 8601 date-time. */
  lastModified: string;
}

/**
 * The closed, public category for a rejected upload (ADR 0009 section 2,
 * amended by ADR 0011: the scan now runs during the upload, synchronously,
 * so this reason only ever reaches the SPA through a 422 UploadRejectedBody,
 * never through a listing). Never the scanner's own internal reason,
 * signature or heuristic name. `too_large` is enforced by the portal before
 * the scan ever runs, so it never actually reaches an UploadRejectedBody
 * (see the 413 file_too_large response instead); it stays in this type
 * because it is shared with internal reporting.
 */
export type RejectReason = "malware" | "unscannable" | "too_large" | "timeout";

/** ADR 0010. A folder's own segment name (not the full path); no lastModified (S3 common prefixes have no reliable time). */
export interface FolderInfo {
  name: string;
}

/**
 * ADR 0010: `folder` selects which mount side `path` scopes
 * (PARTNER_TO_LEEFT or LEEFT_TO_PARTNER); the other side always stays at
 * its root.
 */
export type PartnerFolder = "PARTNER_TO_LEEFT" | "LEEFT_TO_PARTNER";

/** GET /files request parameters (partner-scoped, ADR 0010 amendment A2). */
export interface ListFilesParams {
  folder?: PartnerFolder;
  /** A folder path relative to the mount root selected by `folder`. Absent or empty means the root. */
  path?: string;
}

/**
 * GET /files response (partner-scoped): both of the sharing's folders, each
 * a plain listing (ADR 0011: a rejected upload is refused synchronously at
 * upload time and never appears here or anywhere else in the portal; there
 * is no more pending/"scanning" state).
 */
export interface FilesResponse {
  /** LEEFT_TO_PARTNER: list and download. */
  available: FileInfo[];
  /**
   * PARTNER_TO_LEEFT: list and download back a member's own upload (manager
   * decision, ADR 0007 amendment). Field name kept (ADR 0011 section 5)
   * even though the mount is no longer a separate DELIVERED view: it is
   * PARTNER_TO_LEEFT itself, since the scanner writes a clean partner
   * upload there directly.
   */
  delivered: FileInfo[];
  /** ADR 0010. Echoes the request's `path` (empty string at the root). */
  path: string;
  /** ADR 0010. The selected side's (`folder`) immediate subfolders at `path`; empty when `folder` is not given. */
  folders: FolderInfo[];
  /** ADR 0010. True when `folders` was capped at 500 entries. */
  foldersTruncated: boolean;
}

/** POST /folders and /folders/delete, and the staff outbound equivalents (ADR 0010). */
export interface FolderRequest {
  path: string;
}

/**
 * GET /sharings/{code}/delivered response (staff-scoped, ADR 0010): a
 * dedicated route so folder navigation costs its own two listings, not a
 * full GET /sharings/{code}.
 */
export interface DeliveredBrowseResponse {
  path: string;
  folders: FolderInfo[];
  foldersTruncated: boolean;
  delivered: FileInfo[];
}

/** GET /sharings/{code}/outbound response (staff-scoped, ADR 0010 amendment A2). */
export interface OutboundBrowseResponse {
  path: string;
  folders: FolderInfo[];
  foldersTruncated: boolean;
  outbound: FileInfo[];
}

export interface Sharing {
  code: string;
  displayName: string;
  closed: boolean;
  /** CIDR entries; empty means no restriction. */
  allowedIP: string[];
  /** Computed server-side from the member IDs, not a per-row GET /sharings/{code} (SPA review follow-up). */
  memberCount: number;
  /**
   * LEEFT_TO_PARTNER plus the staff view of PARTNER_TO_LEEFT, counted
   * recursively (ADR 0010, manager decision 2026-09-11). Only populated by
   * `GET /sharings` (the list view); every other response reusing this
   * schema (create, detail, allowlist update, close/reopen) reports 0.
   */
  fileCount: number;
  /** Same two views and the same "list view only" caveat as fileCount. */
  folderCount: number;
  /** True when the walk behind fileCount/folderCount hit the 10,000-entry cap; both are then a lower bound. */
  countsTruncated: boolean;
}

export interface Member {
  id: string;
}

/** GET /sharings/{code} response (staff-scoped): the sharing plus members and both staff-visible file listings. */
export interface SharingDetail extends Sharing {
  members: Member[];
  /** PARTNER_TO_LEEFT via the staff mount (scanned deliveries). */
  delivered: FileInfo[];
  /** LEEFT_TO_PARTNER via the staff mount. */
  outbound: FileInfo[];
}

/**
 * The member ID and password, shown exactly once (ADR 0007 section 1, ADR
 * 0008 section 7). Never persisted beyond the component state backing the
 * one-time confirmation dialog.
 */
export interface MemberCredential {
  id: string;
  password: string;
}

/**
 * POST /sharings request body. `displayName` is the only input (manager
 * decision, 2026-09-11): there is no `code` field any more, the server
 * generates one at random (`code_generation_failed` on repeated
 * collision) and it is never shown in the SPA.
 */
export interface CreateSharingRequest {
  displayName: string;
  /** Optional CIDR allowlist, applied at creation. */
  allowedIP?: string[];
}

/** POST /sharings/{code}/allowlist request body. An empty cidrs array clears the allowlist. */
export interface AllowlistRequest {
  cidrs: string[];
}

/** POST /sharings/{code}/outbound/delete request body. */
export interface DeleteFileRequest {
  name: string;
  /** ADR 0010 (amendment A2). A folder path relative to the sharing's LEEFT_TO_PARTNER root. Optional; absent or empty means the root. */
  path?: string;
}

/**
 * Every machine-readable `error` code returned by internal/portal's
 * handlers (docs/api/portal-api.yaml plus the handler source, since the
 * contract does not enumerate every value inline). "unknown" is this
 * client's fallback for a code introduced on the backend before the SPA
 * is updated to recognise it.
 */
export type ApiErrorCode =
  | "invalid_request"
  | "invalid_credentials"
  | "password_change_required"
  | "not_staff"
  | "local_staff_auth_disabled"
  | "sftpgo_unavailable"
  | "internal_error"
  | "forbidden"
  | "session_expired"
  | "sharing_not_found"
  | "invalid_display_name"
  | "code_generation_failed"
  | "invalid_allowlist"
  | "invalid_member_id"
  | "id_collision"
  | "id_generation_failed"
  | "member_not_found"
  | "invalid_folder"
  | "invalid_path"
  | "folder_exists"
  | "folder_limit_reached"
  | "folder_not_found"
  | "folder_not_empty"
  | "folder_busy"
  | "invalid_file_name"
  | "file_too_large"
  | "upload_rejected"
  | "scan_busy"
  | "scan_unavailable"
  | "storage_unavailable"
  | "download_failed"
  | "password_rejected"
  | "cross_site_request"
  | "csrf_invalid"
  | "conflict"
  | "too_many_transfers"
  | "too_many_uploads"
  | "too_many_folder_operations"
  | "upstream_error"
  | "not_found"
  | "unknown";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  /** Present only when `code` is "upload_rejected" (ADR 0011): the closed, public reason category. */
  readonly reason?: RejectReason;
  /**
   * Present only when `code` is "upload_rejected": 32 random hex
   * characters, meaningful only to the security team. Quote it when
   * contacting Leeft about the rejected upload.
   */
  readonly reference?: string;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    uploadRejected?: { reason?: RejectReason; reference?: string },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.reason = uploadRejected?.reason;
    this.reference = uploadRejected?.reference;
  }
}

/** Thrown by an UploadController's promise when `cancel()` is called before the upload finishes. */
export class UploadCancelledError extends Error {
  constructor() {
    super("Upload cancelled.");
    this.name = "UploadCancelledError";
  }
}

/**
 * ADR 0011: `uploadFile` (src/api/httpClient.ts) reports which phase an
 * upload is in. "uploading" is the byte transfer itself (cancellable,
 * `xhr.upload.onprogress`); "checking" starts once the body has been fully
 * sent and lasts until the scanner's verdict comes back (not cancellable
 * from the UI - about 1 to 2 minutes for a 2 GiB file).
 */
export type UploadPhase = "uploading" | "checking";

export interface UploadProgress {
  phase: UploadPhase;
  loaded: number;
  /** Null when the browser could not compute a total (rare: a File's size is normally known upfront). */
  total: number | null;
}

/**
 * 201 UploadResult (ADR 0011): `name` is the name actually stored, which
 * differs from the name the caller sent only when `renamedFrom` is present
 * (a same-name collision, section 6: the scanner suffixes it, for example
 * "report (2).pdf").
 */
export interface UploadResult extends FileInfo {
  renamedFrom?: string;
}

/**
 * 422 response body for a rejected upload (ADR 0011 section 5). Never a
 * signature, a heuristic name, an S3 key, a version ID or an engine name -
 * only the closed `reason` category and a `reference` meaningful to the
 * security team. This client surfaces it through `ApiError.reason` and
 * `ApiError.reference`, not as a separate return value.
 */
export interface UploadRejectedBody {
  error: "upload_rejected";
  reason: RejectReason;
  reference: string;
  message?: string;
}

/** Returned by every upload call: the in-flight promise plus a way to cancel it (ADR 0008 section 1: XMLHttpRequest with the progress event). */
export interface UploadController {
  result: Promise<UploadResult>;
  cancel: () => void;
}
